import { zodResolver } from '@hookform/resolvers/zod';
import Cookie from 'js-cookie';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  FieldErrors,
  FormProvider,
  useFieldArray,
  useForm,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import {
  getGenderOptionsForRestriction,
  normaliseGenderCode,
} from '@common/helpers/genderRestriction';
import { EDIT_DATES_QUERY } from '@features/JourneyPickerV2/constants';
import { useGetSpaClasses } from '@features/spa/hooks/useGetSpaClasses';
import { useGetSpaClassesPage } from '@features/spa/hooks/useGetSpaClassesPage';
import { useGetSpaSettings } from '@features/spa/hooks/useGetSpaSettings';
import { useTranslations } from '@lang';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';
import { guestSelectors } from '@store/guest/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { buildExistingGuests } from '@utils/buildExistingGuests';
import { scrollToFirstError } from '@utils/scrollToFirstError';
import { useExistingGuestSelect } from '@utils/useExistingGuestSelect';
import { useRouter } from 'shared-routing';

import { Guest } from 'shared-types/Basket';
import { SpaClassBookingForm, SpaClassGuestConfig } from 'voyager';

import {
  SpaClassBookingFormValues,
  SpaClassBookingV2Props,
  spaClassBookingFormSchema,
} from './SpaClassBookingV2.types';

export const SpaClassBookingV2: FC<SpaClassBookingV2Props> = ({
  id,
  property,
  language,
  startDateTime,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const dispatch = useDispatch();
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);

  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams);
  const loggedInGuest = useSelector(guestSelectors.getGuest) as Guest | null;
  const showGenderNeutral = useSelector(entitySelectors.isSpaShowGenderNeutralEnabled);
  const basketGuests = (useSelector((state: any) => basketSelectors.getBasketGuests(state)) ||
    []) as Guest[];
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  ) as boolean;
  const currentBasketItems = useSelector(
    (state: Record<string, unknown>) =>
      (basketSelectors.getBasketDetails(state) as { items?: unknown[] })?.items || []
  );

  const arrivalDate = startDateTime
    ? moment(startDateTime).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');

  const {
    data: classes = [],
    isFetching,
    refetch: refetchClasses,
  } = useGetSpaClasses(property.id, language, arrivalDate);
  const { data: classesPage } = useGetSpaClassesPage(property.id, language);
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;
  const propertyModules = Object.keys(property.modulesExcludingDisabled || {});

  const spaClass = useMemo(() => classes.find((c) => c.id === id), [classes, id]);
  const timeslot = useMemo(
    () =>
      startDateTime && spaClass
        ? spaClass.availability?.find((a) => moment(a.startDateTime).isSame(moment(startDateTime)))
        : undefined,
    [spaClass, startDateTime]
  );

  const isUnavailable =
    !isFetching && (!spaClass || !timeslot || timeslot.numberOfSpacesAvailable === 0);
  const isTelephoneOnly = false;

  const handleBack = useCallback(() => {
    router.push({ pathname: Routes.SPA_CLASSES_LIST, query: { arrivalDate } });
  }, [router, arrivalDate]);

  const handleEditSearch = useCallback(() => {
    router.push({
      pathname: Routes.SPA_CLASSES_LIST,
      query: { arrivalDate, [EDIT_DATES_QUERY]: '1' },
    });
  }, [router, arrivalDate]);

  const existingGuests = useMemo(
    () => buildExistingGuests(basketGuests, loggedInGuest),
    [basketGuests, loggedInGuest]
  );

  const genderRestriction = useMemo(() => {
    const options = [
      { value: 'male', label: t('common.male') },
      { value: 'female', label: t('common.female') },
    ];
    if (showGenderNeutral) options.push({ value: 'neutral', label: t('common.genderNeutral') });
    return getGenderOptionsForRestriction(normaliseGenderCode(timeslot?.gender as any), options);
  }, [showGenderNeutral, timeslot?.gender, t]);

  const defaultGender = genderRestriction.defaultGender;
  const genderOptions = genderRestriction.genderOpts;

  const requestedGuests = Math.max(1, journeyParams?.spaAdults || 1);

  const buildGuests = useCallback(
    (count: number) =>
      Array.from({ length: count }, () => ({
        title: '',
        firstName: '',
        lastName: '',
        gender: defaultGender,
      })),
    [defaultGender]
  );

  const form = useForm<SpaClassBookingFormValues>({
    resolver: zodResolver(spaClassBookingFormSchema),
    defaultValues: {
      guests: buildGuests(requestedGuests),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { errors: formErrors } = useFormState({ control: form.control });

  const { append, remove, replace } = useFieldArray({ control: form.control, name: 'guests' });

  const watchedGuests = useWatch({ control: form.control, name: 'guests' });

  const maxGuests = timeslot?.maxBookingsPerClient ?? timeslot?.numberOfSpacesAvailable;

  // The form is seeded from the search before the timeslot is known, so the capacity can only be
  // applied once it arrives. Trim down and never back up, or a refetch would undo a manual change.
  useEffect(() => {
    if (typeof maxGuests !== 'number' || maxGuests < 1) return;
    const current = form.getValues('guests') || [];
    if (current.length <= maxGuests) return;
    replace(current.slice(0, maxGuests));
  }, [maxGuests, form, replace]);

  const handleAddGuest = useCallback(() => {
    append({ title: '', firstName: '', lastName: '', gender: defaultGender });
  }, [append, defaultGender]);

  const guestFieldLabels = useMemo(
    () => ({
      title: t('common.title'),
      firstName: t('common.firstName'),
      lastName: t('common.lastName'),
      gender: t('common.gender'),
    }),
    [t]
  );

  const translateGuestErrors = useCallback(
    (guestErrors?: FieldErrors<SpaClassBookingFormValues['guests'][number]>) => {
      if (!guestErrors) return undefined;
      const messageFor = (field: 'title' | 'firstName' | 'lastName' | 'gender', label: string) =>
        guestErrors[field] ? t('error.fieldRequired', { label }) : undefined;
      const translated = {
        title: messageFor('title', guestFieldLabels.title),
        firstName: messageFor('firstName', guestFieldLabels.firstName),
        lastName: messageFor('lastName', guestFieldLabels.lastName),
        gender: messageFor('gender', guestFieldLabels.gender),
      };
      const hasAny = Object.values(translated).some(Boolean);
      return hasAny ? translated : undefined;
    },
    [t, guestFieldLabels]
  );

  const guestConfigs: SpaClassGuestConfig[] = useMemo(() => {
    const guests = watchedGuests || [];
    const guestErrorsArray =
      (formErrors.guests as unknown as FieldErrors<
        SpaClassBookingFormValues['guests'][number]
      >[]) || [];
    return guests.map((g, i) => ({
      guestNumber: i + 1,
      values: {
        title: g?.title || '',
        firstName: g?.firstName || '',
        lastName: g?.lastName || '',
        gender: g?.gender || '',
      },
      existingGuests,
      selectedGuestId: (g as any)?.selectedGuestId,
      errors: translateGuestErrors(guestErrorsArray?.[i]),
    }));
  }, [watchedGuests, existingGuests, formErrors, translateGuestErrors]);

  const handleGuestChange = (
    guestIndex: number,
    field: 'title' | 'firstName' | 'lastName' | 'gender',
    value: string
  ) => {
    form.setValue(`guests.${guestIndex}.${field}`, value);
  };

  const handleExistingGuestSelect = useExistingGuestSelect({ form: form as any, existingGuests });

  const labels = useMemo(
    () => ({
      title: t('spaClasses.booking.title'),
      subtitle: t('spaClasses.booking.subtitle'),
      guestDetails: {
        subtitle: t('spa.booking.guestDetails.subtitle'),
        dividerText: t('spa.booking.guestDetails.divider'),
        titlePlaceholder: t('spa.booking.guestDetails.selectPlaceholder'),
        titleLabel: t('common.title'),
        firstNameLabel: t('common.firstName'),
        lastNameLabel: t('common.lastName'),
        genderLabel: t('common.gender'),
        guestTitleFormatter: () => '',
      },
      addGuestLabel: t('common.addMoreGuests'),
      removeGuestLabel: t('common.remove'),
      stickyFooter: {
        totalLabel: t('basket.total'),
        ctaLabel: t('spa.book'),
      },
      telephoneOnly: spaSettings?.phoneNumber
        ? {
            title: t('spa.telephoneOnly', { spaEntity: t('common.classes').toLowerCase() }),
            subtitle: t('treatment.callUsOn', { phoneNumber: spaSettings.phoneNumber }),
          }
        : undefined,
      unavailable: {
        title: t('common.unavailable'),
        subtitle: t('spa.package.unavailableDescription', {
          title: spaClass?.name || '',
          date: startDateTime ? moment(startDateTime).format('dddd Do MMMM YYYY') : '',
        }),
        ctaLabel: t('common.editSearch'),
        onCtaClick: handleEditSearch,
        secondaryCtaLabel: t('common.backToModuleRoot', { entity: t('app.spaClasses') }),
        onSecondaryCtaClick: handleBack,
      },
    }),
    [t, spaSettings?.phoneNumber, spaClass?.name, startDateTime, handleBack, handleEditSearch]
  );

  const handleSubmit = form.handleSubmit(
    async (data) => {
      if (!timeslot || !spaClass) return;

      const guestDetails = data.guests.map((g) => ({
        title: g.title,
        firstName: g.firstName,
        lastName: g.lastName,
        gender: g.gender,
        ...(g.loggedInUser ? { loggedInUser: g.loggedInUser } : {}),
        ...(g.externalSpaId ? { externalSpaId: g.externalSpaId } : {}),
      }));

      const parsedItem = {
        quantity: 1,
        type: 'spa-classes',
        checkIn: moment(timeslot.startDateTime).format(),
        checkOut: moment(timeslot.endDateTime).format(),
        guestDetails,
        bookable: {
          ids: {
            id: spaClass.id,
            type: 'spa-classes',
          },
        },
      };

      const basketId = Cookie.get('basketId');

      try {
        if (basketId) {
          await dispatch(
            updateBasket(
              property.id,
              basketId,
              language,
              { items: [...currentBasketItems, parsedItem] },
              false,
              true
            ) as any
          );
        } else {
          await dispatch(createBasket(property.id, language, { items: [parsedItem] }, true) as any);
        }
        setIsBasketDrawerOpen(true);
      } catch {
        refetchClasses();
      }
    },
    () => {
      scrollToFirstError();
    }
  );

  const handleCloseBasketDrawer = useCallback(() => {
    setIsBasketDrawerOpen(false);
    form.reset({
      guests: buildGuests(Math.min(requestedGuests, maxGuests ?? requestedGuests)),
    });
    refetchClasses();
  }, [form, buildGuests, requestedGuests, maxGuests, refetchClasses]);

  const price = timeslot?.price?.amount ?? spaClass?.price?.amount ?? 0;
  const currencyCode = timeslot?.price?.currencyCode || spaClass?.price?.currencyCode || 'GBP';
  const stickyFooterTitle = [
    spaClass?.name,
    timeslot?.startDateTime && moment(timeslot.startDateTime).format('HH:mm'),
  ]
    .filter(Boolean)
    .join(' - ');
  if (isFetching && !spaClass) {
    return <Spinner isActive showDelayMessage />;
  }

  const fallbackImageUrl = classesPage?.heroBackgroundImage?.url;

  return (
    <FormProvider {...form}>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <SpaClassBookingForm
        imageUrl={spaClass?.image?.url || fallbackImageUrl}
        imageAlt={spaClass?.name}
        onBack={handleBack}
        backLabel={t('spa.booking.goBack')}
        isLoading={isFetching}
        telephoneOnly={isTelephoneOnly}
        unavailable={isUnavailable}
        guests={guestConfigs}
        maxGuests={maxGuests}
        onAddGuest={handleAddGuest}
        onRemoveGuest={remove}
        onExistingGuestSelect={handleExistingGuestSelect}
        onGuestChange={handleGuestChange}
        titleOptions={['Mr', 'Mrs', 'Ms', 'Miss', 'Dr']}
        genderOptions={genderOptions}
        stickyFooter={
          !isUnavailable
            ? {
                title: stickyFooterTitle,
                totalAmount: price * guestConfigs.length,
                currencyCode,
                onCtaClick: handleSubmit,
              }
            : undefined
        }
        labels={labels}
      />
      <BasketDrawerVersion
        open={isBasketDrawerOpen}
        onClose={handleCloseBasketDrawer}
        propertyModules={propertyModules}
        hideArrivalTimes={hideArrivalTimes}
      />
    </FormProvider>
  );
};
